import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  Buildings, Key, ChartBar, Globe, Gear, CreditCard, Copy, Eye, EyeSlash,
  ArrowRight, CheckCircle, Warning, Coins, Certificate, ShieldCheck,
  ArrowClockwise, Trash, Plus, Lightning, Palette, EnvelopeSimple, Link
} from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyDashboardPage = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [usage, setUsage] = useState(null);
  const [apiDocs, setApiDocs] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // API Key visibility
  const [showLiveKey, setShowLiveKey] = useState(false);
  const [showTestKey, setShowTestKey] = useState(false);
  
  // Registration form
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    industry: 'general',
    website: ''
  });
  
  // Webhook form
  const [webhookForm, setWebhookForm] = useState({
    url: '',
    events: ['certification.created', 'certification.verified', 'audit.completed']
  });
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    brand_name: '',
    brand_logo_url: '',
    brand_color_primary: '#2C3E2D',
    brand_color_secondary: '#D4A373',
    certificate_footer_text: ''
  });

  // Check for stored company ID
  const companyId = localStorage.getItem('ginsgood_company_id');

  const fetchCompanyData = useCallback(async () => {
    try {
      const [companyRes, dashboardRes, usageRes, docsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}`),
        axios.get(`${API}/companies/${companyId}/dashboard`),
        axios.get(`${API}/companies/${companyId}/usage`),
        axios.get(`${API}/companies/${companyId}/api-docs`),
        axios.get(`${API}/companies/${companyId}/webhook/events?limit=20`).catch(() => ({ data: { events: [] } }))
      ]);
      
      setCompany(companyRes.data);
      setDashboard(dashboardRes.data);
      setUsage(usageRes.data);
      setApiDocs(docsRes.data);
      setWebhookEvents(eventsRes.data.events || []);
      
      // Pre-fill settings if they exist
      if (companyRes.data.settings) {
        setSettingsForm({ ...settingsForm, ...companyRes.data.settings });
      }
      if (companyRes.data.webhook_url) {
        setWebhookForm({
          url: companyRes.data.webhook_url,
          events: companyRes.data.webhook_events || []
        });
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    } else {
      setLoading(false);
    }
  }, [companyId, fetchCompanyData]);

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/companies/register`, null, {
        params: registerForm
      });
      
      if (response.data.success) {
        localStorage.setItem('ginsgood_company_id', response.data.company_id);
        localStorage.setItem('ginsgood_api_key_live', response.data.api_key_live);
        localStorage.setItem('ginsgood_api_key_test', response.data.api_key_test);
        
        toast.success('Company registered successfully!');
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleCopyKey = (key, type) => {
    navigator.clipboard.writeText(key);
    toast.success(`${type} API key copied to clipboard`);
  };

  const handleRegenerateKey = async (keyType) => {
    if (!confirm(`Are you sure you want to regenerate the ${keyType} API key? The old key will stop working immediately.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/companies/${companyId}/regenerate-api-key?key_type=${keyType}`);
      if (response.data.success) {
        toast.success(`${keyType} API key regenerated`);
        localStorage.setItem(`ginsgood_api_key_${keyType}`, response.data.new_api_key);
        fetchCompanyData();
      }
    } catch (error) {
      toast.error('Failed to regenerate key');
    }
  };

  const handleConfigureWebhook = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/companies/${companyId}/webhook`, null, {
        params: {
          webhook_url: webhookForm.url,
          events: webhookForm.events
        }
      });
      
      if (response.data.success) {
        toast.success('Webhook configured successfully');
        fetchCompanyData();
      }
    } catch (error) {
      toast.error('Failed to configure webhook');
    }
  };

  const handleTestWebhook = async () => {
    try {
      const response = await axios.post(`${API}/companies/${companyId}/webhook/test`);
      if (response.data.success) {
        toast.success('Test webhook sent successfully');
      } else {
        toast.error(response.data.error || 'Webhook test failed');
      }
    } catch (error) {
      toast.error('Failed to send test webhook');
    }
  };

  const handleRemoveWebhook = async () => {
    if (!confirm('Are you sure you want to remove the webhook configuration?')) return;
    
    try {
      await axios.delete(`${API}/companies/${companyId}/webhook`);
      toast.success('Webhook removed');
      setWebhookForm({ url: '', events: [] });
      fetchCompanyData();
    } catch (error) {
      toast.error('Failed to remove webhook');
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/companies/${companyId}/settings`, settingsForm);
      if (response.data.success) {
        toast.success('Settings updated successfully');
        fetchCompanyData();
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const handleUpgrade = async (tier) => {
    try {
      const response = await axios.put(`${API}/companies/${companyId}/upgrade?tier=${tier}`);
      if (response.data.success) {
        toast.success(`Upgraded to ${tier} tier!`);
        fetchCompanyData();
      }
    } catch (error) {
      toast.error('Failed to upgrade');
    }
  };

  // Registration screen for new companies
  if (!companyId && !loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-xl mx-auto px-6">
            <Card className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Buildings weight="duotone" className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Company Registration
                </h1>
                <p className="text-muted-foreground">
                  Register your company to get API access for certification integration
                </p>
              </div>

              <form onSubmit={handleRegister} className="space-y-6">
                <div>
                  <Label>Company Name *</Label>
                  <Input
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    placeholder="Your Company Ltd."
                    required
                    data-testid="company-name"
                  />
                </div>

                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    placeholder="api@yourcompany.com"
                    required
                    data-testid="company-email"
                  />
                </div>

                <div>
                  <Label>Industry</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={registerForm.industry}
                    onChange={(e) => setRegisterForm({ ...registerForm, industry: e.target.value })}
                    data-testid="company-industry"
                  >
                    <option value="general">General</option>
                    <option value="textiles">Textiles & Apparel</option>
                    <option value="jewelry">Jewelry & Accessories</option>
                    <option value="leather">Leather & Bags</option>
                    <option value="home">Home & Furniture</option>
                  </select>
                </div>

                <div>
                  <Label>Website (Optional)</Label>
                  <Input
                    value={registerForm.website}
                    onChange={(e) => setRegisterForm({ ...registerForm, website: e.target.value })}
                    placeholder="https://yourcompany.com"
                    data-testid="company-website"
                  />
                </div>

                <Button type="submit" className="w-full h-12 rounded-full" data-testid="register-company-btn">
                  Register & Get API Keys
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t">
                <h3 className="font-medium mb-3">What You'll Get:</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-primary" />
                    <span>Live & Test API keys</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Certificate className="w-4 h-4 text-primary" />
                    <span>100 certifications/month (Starter tier)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link className="w-4 h-4 text-primary" />
                    <span>Webhook integration (Professional+)</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-6xl mx-auto px-6 text-center">
            <div className="animate-pulse">Loading dashboard...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const tier = dashboard?.company?.subscription_tier || 'starter';
  const usagePercent = usage ? (usage.current_month_usage / usage.usage_limit) * 100 : 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Badge variant="outline" className="mb-2">
                <Buildings weight="duotone" className="w-4 h-4 mr-2" />
                Company Dashboard
              </Badge>
              <h1 className="text-3xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                {company?.name || 'Your Company'}
              </h1>
              <p className="text-muted-foreground">{company?.email}</p>
            </div>
            <Badge 
              className={`text-base px-4 py-2 ${
                tier === 'enterprise' ? 'bg-purple-500' :
                tier === 'professional' ? 'bg-blue-500' : 'bg-gray-500'
              }`}
            >
              {tier.charAt(0).toUpperCase() + tier.slice(1)} Tier
            </Badge>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <TabsList className="grid w-full max-w-2xl grid-cols-5">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="api-keys" data-testid="tab-api-keys">API Keys</TabsTrigger>
              <TabsTrigger value="webhooks" data-testid="tab-webhooks">Webhooks</TabsTrigger>
              <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
              <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid md:grid-cols-4 gap-4 mb-8">
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Certificate className="w-5 h-5 text-primary" />
                    <span className="text-sm text-muted-foreground">Certificates</span>
                  </div>
                  <p className="text-2xl font-bold">{dashboard?.statistics?.total_certificates || 0}</p>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <ChartBar className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-muted-foreground">Avg Score</span>
                  </div>
                  <p className="text-2xl font-bold">{dashboard?.statistics?.average_score || 0}%</p>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <ShieldCheck className="w-5 h-5 text-blue-500" />
                    <span className="text-sm text-muted-foreground">Audits</span>
                  </div>
                  <p className="text-2xl font-bold">{dashboard?.statistics?.completed_audits || 0}</p>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Lightning className="w-5 h-5 text-yellow-500" />
                    <span className="text-sm text-muted-foreground">This Month</span>
                  </div>
                  <p className="text-2xl font-bold">{usage?.current_month_usage || 0}</p>
                </Card>
              </div>

              {/* Usage Progress */}
              <Card className="p-6 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium">Monthly Usage</h3>
                  <span className="text-sm text-muted-foreground">
                    {usage?.current_month_usage || 0} / {usage?.usage_limit || 100} certifications
                  </span>
                </div>
                <Progress value={usagePercent} className="h-3" />
                <p className="text-sm text-muted-foreground mt-2">
                  {usagePercent < 80 ? 'Usage is healthy' : 
                   usagePercent < 100 ? 'Approaching limit - consider upgrading' : 
                   'Limit reached - upgrade to continue'}
                </p>
              </Card>

              {/* Tier Distribution */}
              <Card className="p-6">
                <h3 className="font-medium mb-4">Certificate Tiers</h3>
                <div className="grid grid-cols-5 gap-4">
                  {['platinum', 'gold', 'silver', 'bronze', 'uncertified'].map(tierKey => (
                    <div key={tierKey} className="text-center">
                      <div className={`text-2xl font-bold ${
                        tierKey === 'platinum' ? 'text-purple-500' :
                        tierKey === 'gold' ? 'text-yellow-500' :
                        tierKey === 'silver' ? 'text-gray-400' :
                        tierKey === 'bronze' ? 'text-orange-600' : 'text-gray-300'
                      }`}>
                        {dashboard?.statistics?.tier_distribution?.[tierKey] || 0}
                      </div>
                      <p className="text-xs text-muted-foreground capitalize">{tierKey}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            {/* API Keys Tab */}
            <TabsContent value="api-keys">
              <div className="space-y-6">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Key className="w-5 h-5 text-green-500" />
                      <h3 className="font-medium">Live API Key</h3>
                      <Badge className="bg-green-100 text-green-800">Production</Badge>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRegenerateKey('live')}
                      data-testid="regenerate-live-key"
                    >
                      <ArrowClockwise className="w-4 h-4 mr-1" />
                      Regenerate
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type={showLiveKey ? "text" : "password"}
                      value={localStorage.getItem('ginsgood_api_key_live') || usage?.api_keys?.live || ''}
                      readOnly
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowLiveKey(!showLiveKey)}
                    >
                      {showLiveKey ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopyKey(localStorage.getItem('ginsgood_api_key_live'), 'Live')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Use this key for production API calls. Charges apply.
                  </p>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Key className="w-5 h-5 text-orange-500" />
                      <h3 className="font-medium">Test API Key</h3>
                      <Badge className="bg-orange-100 text-orange-800">Sandbox</Badge>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRegenerateKey('test')}
                      data-testid="regenerate-test-key"
                    >
                      <ArrowClockwise className="w-4 h-4 mr-1" />
                      Regenerate
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type={showTestKey ? "text" : "password"}
                      value={localStorage.getItem('ginsgood_api_key_test') || usage?.api_keys?.test || ''}
                      readOnly
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowTestKey(!showTestKey)}
                    >
                      {showTestKey ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleCopyKey(localStorage.getItem('ginsgood_api_key_test'), 'Test')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Use this key for testing and development. No charges.
                  </p>
                </Card>

                {/* API Documentation */}
                <Card className="p-6">
                  <h3 className="font-medium mb-4">Quick Start</h3>
                  <div className="bg-muted rounded-lg p-4 font-mono text-sm overflow-x-auto">
                    <pre>{`curl -X POST "${API}/certify" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "product_name": "Product Name",
    "seller_iris_id": "verified-iris-id",
    ...
  }'`}</pre>
                  </div>
                  <Button 
                    variant="link" 
                    className="mt-4 p-0"
                    onClick={() => navigate('/docs')}
                  >
                    View Full API Documentation
                  </Button>
                </Card>
              </div>
            </TabsContent>

            {/* Webhooks Tab */}
            <TabsContent value="webhooks">
              {tier === 'starter' ? (
                <Card className="p-8 text-center">
                  <Link className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Webhooks Available on Professional+</h3>
                  <p className="text-muted-foreground mb-4">
                    Upgrade to Professional or Enterprise to receive real-time events.
                  </p>
                  <Button onClick={() => setActiveTab('billing')}>
                    Upgrade Now
                  </Button>
                </Card>
              ) : (
                <div className="space-y-6">
                  <Card className="p-6">
                    <h3 className="font-medium mb-4">Webhook Configuration</h3>
                    <form onSubmit={handleConfigureWebhook} className="space-y-4">
                      <div>
                        <Label>Webhook URL</Label>
                        <Input
                          type="url"
                          value={webhookForm.url}
                          onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                          placeholder="https://yourserver.com/webhook"
                          required
                          data-testid="webhook-url"
                        />
                      </div>

                      <div>
                        <Label className="mb-2 block">Events to Subscribe</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {['certification.created', 'certification.verified', 'audit.completed', 'supply_chain.updated'].map(event => (
                            <label key={event} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted">
                              <input
                                type="checkbox"
                                checked={webhookForm.events.includes(event)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setWebhookForm({ ...webhookForm, events: [...webhookForm.events, event] });
                                  } else {
                                    setWebhookForm({ ...webhookForm, events: webhookForm.events.filter(ev => ev !== event) });
                                  }
                                }}
                              />
                              <span className="text-sm">{event}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1" data-testid="save-webhook">
                          Save Webhook
                        </Button>
                        {company?.webhook_url && (
                          <>
                            <Button type="button" variant="outline" onClick={handleTestWebhook}>
                              Test
                            </Button>
                            <Button type="button" variant="destructive" onClick={handleRemoveWebhook}>
                              <Trash className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </form>
                  </Card>

                  {/* Webhook Events */}
                  <Card className="p-6">
                    <h3 className="font-medium mb-4">Recent Events</h3>
                    {webhookEvents.length === 0 ? (
                      <p className="text-muted-foreground text-center py-4">No webhook events yet</p>
                    ) : (
                      <div className="space-y-2">
                        {webhookEvents.map(event => (
                          <div key={event.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${
                                event.status === 'sent' ? 'bg-green-500' : 
                                event.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                              }`} />
                              <span className="font-mono text-sm">{event.event_type}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{event.status}</span>
                              {event.response_code && <Badge variant="outline">{event.response_code}</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              {tier !== 'enterprise' ? (
                <Card className="p-8 text-center">
                  <Palette className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">White-Label Available on Enterprise</h3>
                  <p className="text-muted-foreground mb-4">
                    Customize branding, colors, and certificate appearance.
                  </p>
                  <Button onClick={() => setActiveTab('billing')}>
                    Upgrade to Enterprise
                  </Button>
                </Card>
              ) : (
                <Card className="p-6">
                  <h3 className="font-medium mb-4">White-Label Settings</h3>
                  <form onSubmit={handleUpdateSettings} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Brand Name</Label>
                        <Input
                          value={settingsForm.brand_name}
                          onChange={(e) => setSettingsForm({ ...settingsForm, brand_name: e.target.value })}
                          placeholder="Your Brand"
                          data-testid="brand-name"
                        />
                      </div>
                      <div>
                        <Label>Logo URL</Label>
                        <Input
                          value={settingsForm.brand_logo_url}
                          onChange={(e) => setSettingsForm({ ...settingsForm, brand_logo_url: e.target.value })}
                          placeholder="https://..."
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Primary Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={settingsForm.brand_color_primary}
                            onChange={(e) => setSettingsForm({ ...settingsForm, brand_color_primary: e.target.value })}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={settingsForm.brand_color_primary}
                            onChange={(e) => setSettingsForm({ ...settingsForm, brand_color_primary: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Secondary Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={settingsForm.brand_color_secondary}
                            onChange={(e) => setSettingsForm({ ...settingsForm, brand_color_secondary: e.target.value })}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={settingsForm.brand_color_secondary}
                            onChange={(e) => setSettingsForm({ ...settingsForm, brand_color_secondary: e.target.value })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Certificate Footer Text</Label>
                      <Input
                        value={settingsForm.certificate_footer_text}
                        onChange={(e) => setSettingsForm({ ...settingsForm, certificate_footer_text: e.target.value })}
                        placeholder="Powered by Your Company"
                      />
                    </div>

                    <Button type="submit" data-testid="save-settings">
                      Save Settings
                    </Button>
                  </form>
                </Card>
              )}
            </TabsContent>

            {/* Billing Tab */}
            <TabsContent value="billing">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Starter */}
                <Card className={`p-6 ${tier === 'starter' ? 'border-2 border-primary' : ''}`}>
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-medium">Starter</h3>
                    <p className="text-3xl font-bold mt-2">Free</p>
                    <p className="text-muted-foreground">to get started</p>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      100 certifications/month
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Basic API access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Email support
                    </li>
                  </ul>
                  {tier === 'starter' ? (
                    <Button disabled className="w-full">Current Plan</Button>
                  ) : (
                    <Button variant="outline" className="w-full" disabled>Downgrade</Button>
                  )}
                </Card>

                {/* Professional */}
                <Card className={`p-6 relative ${tier === 'professional' ? 'border-2 border-primary' : ''}`}>
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-blue-500">Popular</Badge>
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-medium">Professional</h3>
                    <p className="text-3xl font-bold mt-2">$99</p>
                    <p className="text-muted-foreground">/month</p>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      1,000 certifications/month
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Webhooks & events
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Supply chain tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Audit requests
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Priority support
                    </li>
                  </ul>
                  {tier === 'professional' ? (
                    <Button disabled className="w-full">Current Plan</Button>
                  ) : tier === 'enterprise' ? (
                    <Button variant="outline" className="w-full" disabled>Downgrade</Button>
                  ) : (
                    <Button className="w-full" onClick={() => handleUpgrade('professional')} data-testid="upgrade-professional">
                      Upgrade
                    </Button>
                  )}
                </Card>

                {/* Enterprise */}
                <Card className={`p-6 ${tier === 'enterprise' ? 'border-2 border-primary' : ''}`}>
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-medium">Enterprise</h3>
                    <p className="text-3xl font-bold mt-2">$499</p>
                    <p className="text-muted-foreground">/month</p>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Unlimited certifications
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      White-label branding
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Custom domain
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Dedicated support
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      SLA guarantee
                    </li>
                  </ul>
                  {tier === 'enterprise' ? (
                    <Button disabled className="w-full">Current Plan</Button>
                  ) : (
                    <Button className="w-full bg-purple-500 hover:bg-purple-600" onClick={() => handleUpgrade('enterprise')} data-testid="upgrade-enterprise">
                      Upgrade
                    </Button>
                  )}
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CompanyDashboardPage;
